import { useMemo, useState } from 'react'
import { Heading, Tabs } from '@design-systems/canvas-ui'
import { useBrand } from '../../../shared/brand/context/BrandContext'
import ConfigurationSection from '../components/ConfigurationSection'
import SkillInstructionsModal from '../components/SkillInstructionsModal'
import {
  BRAND_CATEGORY_SECTION,
  DOCUMENT_TYPE_SECTION,
  SETTINGS_TAB_TYPES,
  SKILLS_INSTRUCTIONS_SECTION,
  settingsTabs,
} from '../config/settingsConfig'
import './SettingsPage.css'

const bindSectionActions = (section, actionHandlers) => ({
  ...section,
  actionConfig: {
    ...section.actionConfig,
    actions: (section.actionConfig?.actions ?? []).map((action) => ({
      ...(typeof action === 'string' ? { label: action } : action),
      onClick:
        typeof action === 'string' ? undefined : actionHandlers[action.id],
    })),
  },
})

const SettingsPage = () => {
  const [selectedSkillInstruction, setSelectedSkillInstruction] = useState(null)
  const {
    allCategories,
    allDocumentTypes,
    allSkillInstructions,
    createBrandCategory,
    createDocumentType,
    createSkillInstruction,
    updateSkillInstruction,
    documentTypeError,
    skillInstructionError,
    isSkillInstructionsLoading,
    error,
    isDocumentTypesLoading,
    isLoading,
    updateBrandCategory,
    updateDocumentType,
  } = useBrand()

  const actionHandlers = useMemo(
    () => ({
      'update-instructions': (skillInstruction) =>
        setSelectedSkillInstruction(skillInstruction),
    }),
    [],
  )

  const skillInstructionsSection = useMemo(
    () => bindSectionActions(SKILLS_INSTRUCTIONS_SECTION, actionHandlers),
    [actionHandlers],
  )

  const sectionsByTabType = {
    [SETTINGS_TAB_TYPES.BRAND_CATEGORIES]: (
      <ConfigurationSection
        error={error}
        isLoading={isLoading}
        items={allCategories}
        onCreate={createBrandCategory}
        onUpdate={updateBrandCategory}
        section={BRAND_CATEGORY_SECTION}
      />
    ),
    [SETTINGS_TAB_TYPES.DOCUMENT_TYPES]: (
      <ConfigurationSection
        error={documentTypeError}
        isLoading={isDocumentTypesLoading}
        items={allDocumentTypes}
        onCreate={createDocumentType}
        onUpdate={updateDocumentType}
        section={DOCUMENT_TYPE_SECTION}
      />
    ),
    [SETTINGS_TAB_TYPES.SKILLS_INSTRUCTIONS]: (
      <ConfigurationSection
        error={skillInstructionError}
        isLoading={isSkillInstructionsLoading}
        items={allSkillInstructions}
        onCreate={createSkillInstruction}
        onUpdate={updateSkillInstruction}
        section={skillInstructionsSection}
      />
    ),
  }

  return (
    <section className="settings-page">
      <div className="settings-page__header">
        <div>
          <Heading as="h1" size="lg">
            Settings
          </Heading>
          <p>Manage the configuration used across Brand DNA.</p>
        </div>
      </div>

      <Tabs defaultTab={settingsTabs[0].id}>
        {settingsTabs.map((tab) => (
          <Tabs.Item key={tab.id} name={tab.id}>
            {tab.label}
          </Tabs.Item>
        ))}
        {settingsTabs.map((tab) => (
          <Tabs.Content key={tab.id} name={tab.id}>
            {sectionsByTabType[tab.type] || (
              <div className="settings-page__empty-tab" role="status">
                <Heading as="h2" size="md">
                  No data available
                </Heading>
                <p>{tab.emptyMessage}</p>
              </div>
            )}
          </Tabs.Content>
        ))}
      </Tabs>
      <SkillInstructionsModal
        isOpen={Boolean(selectedSkillInstruction)}
        mode={selectedSkillInstruction}
        onClose={() => setSelectedSkillInstruction(null)}
      />
    </section>
  )
}

export default SettingsPage
